import { useCallback, useEffect, useState } from 'react';
import Layout from '../../components/Layout/Layout';
import { GetFilterUrl, showToastNotification } from '../../utility/helper';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import ImagePreview from '@/components/common/media/ImagePreview';
import { PageInfo } from '@/interfaces/common.interface';
import { callApi, ThrowError } from '@/api/callApi';
import DragDropList from '@/components/common/commonTable/DragDropList';
import {
  IBrandsResponse,
  IBrandsResponseList,
} from '@/interfaces/brandsResponse.interface';
import Pagination from '@/components/common/Pagination';
import Card from '@/components/UI/Card';

interface StateProps {
  brands: IBrandsResponse[];
  loading: boolean;
  saving: boolean;
  pageInfo: PageInfo;
}

const BrandManager = () => {
  const [states, setStates] = useState<StateProps>({
    loading: false,
    brands: [],
    saving: false,
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
  });

  const handleChangeState = (newState: Partial<StateProps>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const getBrands = useCallback(async () => {
    handleChangeState({ loading: true });
    try {
      const filterQuery = {
        sort: 'order_index',
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
      };
      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}?${query ? query : ''}`;
      const response = await callApi<IBrandsResponseList>(PATH, {
        method: 'GET',
      });
      const normalized = response.data
        .map((b, i: number) => ({
          ...b,
          orderIndex: b.orderIndex ?? i,
        }))
        .sort((a, b) => a.orderIndex - b.orderIndex);

      handleChangeState({
        brands: normalized,
        pageInfo: response.pageInfo,
      });
    } catch (error) {
      ThrowError(error);
    } finally {
      handleChangeState({ loading: false });
    }
  }, [states.pageInfo.page, states.pageInfo.limit]);

  useEffect(() => {
    getBrands();
  }, [getBrands]);

  const saveBrands = (items: IBrandsResponse[]) => {
    handleChangeState({
      brands: items.map((item, index) => ({
        ...item,
        orderIndex: index + 1,
      })),
    });
  };

  const saveOrder = async () => {
    handleChangeState({ saving: true });
    try {
      const payload = states.brands.map((b) => ({
        id: b.id,
        order: b.orderIndex,
      }));
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}/reorder/items`;
      await callApi(PATH, {
        method: 'PATCH',
        data: payload,
      });

      showToastNotification('success', 'Brands orderIndex saved successfully');
    } catch (error) {
      ThrowError(error);
    } finally {
      handleChangeState({ saving: false });
    }
  };

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    });
  };

  const handleChangePageSize = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page: 1,
        limit,
      },
    });
  };

  return (
    <Layout>
      <Card>
        <DragDropList
          title="Primary Brands"
          data={states.brands}
          isLoading={states.loading}
          getId={(item) => item.id}
          renderItem={(item) => (
            <div className="flex items-center gap-3 w-full">
              <ImagePreview
                src={`${API_URL_PRODUCT_SERVICE}/${item?.media?.[0]?.url}`}
                text={item.name}
              />
              <span className="ml-auto text-muted">#{item.orderIndex}</span>
            </div>
          )}
          onReorder={saveBrands}
          onSave={saveOrder}
          saving={states.saving}
        />
        <Pagination
          isLoading={states.loading}
          paginationData={states.pageInfo}
          onPageChange={handleChangePage}
          onPageSizeChange={handleChangePageSize}
        />
      </Card>
    </Layout>
  );
};

export default BrandManager;
